import { BybButton } from "@Components/common/byb-ds";
import { ScenarioFilterDialog } from "@Components/game/scenario-editor";
import { Filter } from "@Components/game/scenario-editor/scenario-filters-dialog";
import Icon from "@Components/generic/icon";
import PhaseOfPlay from "@Enums/PhaseOfPlay";
import PlayPositions from "@Enums/PlayPositions";
import { faSearch, faSliders } from "@fortawesome/pro-solid-svg-icons";
import { useApi, useGlobalContext } from "@hooks";
import useI18n from "@i18n";
import { Box, CircularProgress, DialogProps, Stack, Tab, Tabs, TextField, Typography, styled, useTheme } from "@mui/material";
import { ContentTagTypes } from "@Shared/enums";
import { Api } from "@Shared/schemas";
import { PlaylistScenario } from "@Shared/schemas/playlists/playlistSchemas";
import { ScenarioResponse } from "@Shared/schemas/scenarios/scenarioSchemas";
import { isBoolean } from "lodash-es";
import { useEffect, useState } from "react";
import { useDebounce } from "react-use";
import { BybDialog } from "../../../common";
import TabContent from "./tab-content";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const StyledTab = styled(Tab)(({ theme }) => ({
  flex: 1,
  ...theme.typography.textLg600,
  textTransform: "capitalize",
}));

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;
  const theme = useTheme();

  return (
    <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
      {value === index && <Box sx={{ padding: theme.spacing(4, 0) }}>{children}</Box>}
    </div>
  );
};

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

interface IPlaylistScenarioSelectionDialog extends Omit<DialogProps, "onSelect"> {
  onClose: () => void;
  onSelect: (scenarios: ScenarioResponse[]) => void;
  existingScenarios?: PlaylistScenario[];
}

const PlaylistScenarioSelectionDialog = ({ onClose, onSelect, existingScenarios = [], ...props }: IPlaylistScenarioSelectionDialog) => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const { globalState } = useGlobalContext();
  const [selectedScenarios, setSelectedScenarios] = useState<ScenarioResponse[]>([]);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [throttledSearchQuery, setThrottledSearchQuery] = useState("");
  const [throttledLoading, setThrottledLoading] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filter, setFilter] = useState<Filter | undefined>();
  const [hasFilters, setHasFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [scenarios, setScenarios] = useState<ScenarioResponse[]>([]);
  const [scenarioTotal, setScenarioTotal] = useState(0);

  const [, cancel] = useDebounce(
    () => {
      setThrottledSearchQuery(searchQuery);
    },
    700,
    [searchQuery]
  );

  const {
    call: fetchScenarios,
    data: fetchScenarioResponse,
    loading,
  } = useApi<typeof Api.Scenarios.GetScenarios>({
    api: Api.Scenarios.GetScenarios,
    request: {
      account_id: tabValue === 0 ? (globalState.account?.id! as number) : null,
      search_query: throttledSearchQuery,
      filter_published: isBoolean(filter?.published) ? filter.published : undefined,
      filter_phase_of_play: filter?.phase_of_play as PhaseOfPlay | undefined,
      filter_tags: tabValue === 1 ? [ContentTagTypes.General] : (filter?.tags as string[]),
      filter_play_positions: filter?.play_positions as PlayPositions[] | undefined,
      offset: (page - 1) * pageSize,
      limit: pageSize,
      include_public: tabValue === 1,
    },
  });

  useEffect(() => {
    if (fetchScenarioResponse) {
      setScenarios(fetchScenarioResponse.scenarios);
      setScenarioTotal(fetchScenarioResponse.total);
    } else {
      setPage(1);
      setScenarios([]);
      setScenarioTotal(0);
    }
  }, [fetchScenarioResponse]);

  useEffect(() => {
    setHasFilters(filter ? Object.values(filter).some((value) => value !== undefined) : false);
  }, [filter]);

  const [, cancelLoading] = useDebounce(
    () => {
      setThrottledLoading(loading);
    },
    500,
    [loading]
  );

  useEffect(() => {
    setPage(1);
  }, [throttledSearchQuery, filter, tabValue]);

  useEffect(() => {
    fetchScenarios();
  }, [throttledSearchQuery, page, pageSize, filter, tabValue]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedScenarios([]);
  };

  const handleScenarioSelect = (scenario: ScenarioResponse) => {
    const isSelected = selectedScenarios.some((s) => s.id === scenario.id);
    if (isSelected) {
      setSelectedScenarios((prev) => prev.filter((s) => s.id !== scenario.id));
    } else {
      setSelectedScenarios((prev) => [...prev, scenario]);
    }
  };

  return (
    <BybDialog
      title="Select scenarios"
      name="playlist-scenario-selection-dialog"
      onClose={onClose}
      {...props}
      sx={{
        "& .MuiDialog-paper": {
          minHeight: "90vh",
          minWidth: theme.spacing(76),
          overflow: "auto",
          [theme.breakpoints.down("lg")]: {
            minWidth: theme.spacing(76),
          },
        },
      }}
      dialogActions={
        <>
          <BybButton onClick={onClose} color="secondary" fullWidth>
            Cancel
          </BybButton>
          <BybButton
            onClick={() => {
              onSelect(selectedScenarios);
              onClose();
            }}
            color="primary"
            fullWidth
          >
            {`Add (${selectedScenarios.length})`}
          </BybButton>
        </>
      }
    >
      <Stack spacing={3}>
        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleChange} aria-label="scenario tabs">
            <StyledTab label={i18n("playlists.playlist.selection.your.account")} {...a11yProps(0)} />
            <StyledTab label={i18n("be-your-best")} {...a11yProps(1)} />
          </Tabs>
        </Box>

        {/* Search and Filter Controls */}
        <Stack direction="row" spacing={2} sx={{ padding: theme.spacing(0.2, 0) }}>
          <TextField
            disabled={throttledLoading}
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search scenarios"
            InputProps={{
              startAdornment: <Icon icon={faSearch} style={{ marginRight: theme.spacing(1) }} />,
            }}
            fullWidth
          />
          <BybButton
            disabled={throttledLoading}
            color={hasFilters ? "primary" : "secondary"}
            icon={<Icon icon={faSliders} />}
            onClick={() => setFiltersOpen(true)}
          />
        </Stack>

        {throttledLoading ? (
          <CircularProgress sx={{ alignSelf: "center", margin: theme.spacing(4) }} />
        ) : (
          <>
            <Box
              sx={{
                height: "calc(100vh - 620px)",
                minHeight: theme.spacing(40),
                overflow: "auto",
              }}
            >
              <Typography variant="textLg700" color="dsBase.v200">
                {`Scenarios (${scenarioTotal})`}
              </Typography>
              <CustomTabPanel value={tabValue} index={0}>
                <TabContent scenarios={scenarios} selectedScenarios={selectedScenarios.map((s) => s.id)} onScenarioSelected={handleScenarioSelect} />
              </CustomTabPanel>
              <CustomTabPanel value={tabValue} index={1}>
                <TabContent scenarios={scenarios} selectedScenarios={selectedScenarios.map((s) => s.id)} onScenarioSelected={handleScenarioSelect} />
              </CustomTabPanel>
            </Box>

            {scenarios.length > 0 && (
              <Stack direction="row" alignItems="center" spacing={theme.spacing(2)} justifyContent="right">
                <BybButton color="primary" sx={{ width: "100px" }} onClick={() => setPage((prev) => Math.max(prev - 1, 1))} disabled={page === 1}>
                  {i18n("playlists.playlist.sharing.previous")}
                </BybButton>
                <Typography variant="textBase600" sx={{ color: theme.palette.dsBase.v200 }}>
                  {i18n("playlists.playlist.sharing.page", { page, total: Math.ceil((scenarioTotal || 0) / pageSize) })}
                </Typography>
                <BybButton color="primary" sx={{ width: "100px" }} onClick={() => setPage((prev) => prev + 1)} disabled={scenarioTotal <= pageSize}>
                  {i18n("playlists.playlist.sharing.next")}
                </BybButton>
              </Stack>
            )}
          </>
        )}
      </Stack>

      <ScenarioFilterDialog open={filtersOpen} onApply={(filter: Filter | undefined) => setFilter(filter)} onClose={() => setFiltersOpen(false)} />
    </BybDialog>
  );
};

export default PlaylistScenarioSelectionDialog;
